import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import Spinner from "@legacy/core/components/Spinner";
import InvoiceObjectLink from "@legacy/invoices/components/InvoiceObjectLink";
import PDFDownloadDialog from "@legacy/pricebook/components/PDFDownloadDialog";
import { AccessCheck } from "@particles/index";
import { Component, Fragment } from "react";
import { renderClientString, renderServiceLocationString } from "../../clients/utils/utils";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import AttachmentHorizontalDisplay from "../../core/components/AttachmentHorizontalDisplay";
import Banner from "../../core/components/Banner";
import LineItemListDisplayGroup from "../../core/components/LineItemListDisplayGroup";
import BasicDisplayField from "../../core/fields/BasicDisplayField";
import LinkedObjectsDisplayField from "../../core/fields/LinkedObjectsDisplayField";
import { convertToKebabCase, currencyFormatterForServiceCompany, formatCurrencyForServiceCompany, formatLocalTime, renderInvoiceEstimateDate } from "../../core/utils/utils";
import { getCreditNoteStatusBarLabel } from "../utils/utils";
import CreditNoteFeedAccordion from "./CreditNoteFeedAccordion";


class CreditNoteDetailsCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isPDFDialogOpen: false,
        };
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    handlePDFDownload = (selectedObjects, isPublicVersion) => {
        let url = window.PUBLIC_PDF_URL;
        const includeInvoice = selectedObjects?.includes("Invoice") ? "true" : "false";

        if (selectedObjects?.length === 0 || selectedObjects === undefined && !isPublicVersion) {
            location.assign(url);
        } else {
            url = `${window.PUBLIC_PDF_URL}?include_invoice=${includeInvoice}&public_version=${isPublicVersion ? "true" : "false"}`;
            location.assign(url);
        }

        this.setState({ isPDFDialogOpen: false });
    };

    renderButtons = () => {
        const { creditNote, requestAction, switchToSecondaryForm, submitting, errors } = this.props

        const handlePDFDialog = () => {
            this.setState({ isPDFDialogOpen: !this.state.isPDFDialogOpen })
        };

        if (submitting) {
            return (
                <div className="data-panel__action-feedback">
                    <Spinner centered={true} />
                </div>
            )
        }
        else {
            if (window.USING_PUBLIC_URL === true) {
                return (
                    <ButtonGroup>
                        <ButtonGroupRow>
                            <UniversalButton type="secondary" text="Download PDF" handler={handlePDFDialog} />
                        </ButtonGroupRow>
                    </ButtonGroup>
                )
            }
            else {
                if (creditNote.status_label === "Draft") {
                    return (
                        <ButtonGroup>
                            <AccessCheck permissions={{
                                invoices_create_permission: PERMISSION_LEVEL.FULL
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text="Create and Send" handler={event => requestAction("CREDIT_NOTE_CREATE")} />
                                </ButtonGroupRow>
                            </AccessCheck>
                            <ButtonGroupRow>
                                <UniversalButton type="secondary" text="Download PDF" handler={handlePDFDialog} />
                                <AccessCheck permissions={{
                                    invoices_edit_permission: PERMISSION_LEVEL.RESTRICTED
                                }}>
                                    <UniversalButton type="secondary" text="Edit Draft" handler={event => requestAction("CREDIT_NOTE_EDIT")} />
                                </AccessCheck>
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else if (creditNote.status_label === "Unsent") {
                    return (
                        <ButtonGroup>
                            <AccessCheck permissions={{
                                invoices_edit_permission: PERMISSION_LEVEL.FULL
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="primary" text="Send Credit Note" handler={event => requestAction("CREDIT_NOTE_SEND")} />
                                </ButtonGroupRow>
                            </AccessCheck>
                            <ButtonGroupRow>
                                <UniversalButton type="secondary" text="Download PDF" handler={handlePDFDialog} />
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
                else if (creditNote.status_label === "Issued") {
                    return (
                        <ButtonGroup>
                            <ButtonGroupRow>
                                <AccessCheck permissions={{
                                    invoices_edit_permission: PERMISSION_LEVEL.FULL
                                }}>
                                    <UniversalButton type="secondary" text="Resend Credit Note" handler={event => requestAction("CREDIT_NOTE_SEND")} />
                                </AccessCheck>
                                <UniversalButton type="secondary" text="Download PDF" handler={handlePDFDialog} />
                            </ButtonGroupRow>
                            <AccessCheck permissions={{
                                invoices_delete_permission: PERMISSION_LEVEL.RESTRICTED
                            }}>
                                <ButtonGroupRow>
                                    <UniversalButton type="danger" text="Void" handler={event => requestAction("CREDIT_NOTE_VOID")} />
                                </ButtonGroupRow>
                            </AccessCheck>
                        </ButtonGroup>
                    )
                }
                else if (creditNote.status_label === "Voided") {
                    return (
                        <ButtonGroup>
                            <ButtonGroupRow>
                                <UniversalButton type="secondary" text="Download PDF" handler={handlePDFDialog} />
                            </ButtonGroupRow>
                        </ButtonGroup>
                    )
                }
            }
        }
    }

    renderLastViewed = (lastViewed, preferredTimezone) => {
        const { localDateTimeString } = formatLocalTime(lastViewed, preferredTimezone, false, true, false, true)
        return localDateTimeString
    }

    render() {
        const { creditNote, requestAction, switchToSecondaryForm, submitting, errors, addToastToQueue } = this.props

        const totalAllocated = (creditNote.credit_allocations || []).reduce((sum, allocation) => sum + parseFloat(allocation.amount), 0)
        const appliedToBalance = parseFloat(creditNote.total) - totalAllocated

        return (
            <Fragment>
                <div id="credit_note_details_react_wrapper">
                    <div className="data-panel-container">
                        <div className={`data-panel-status-bar credit-notes-${convertToKebabCase(creditNote.status_label)}`} aria-label="Status"><span>{getCreditNoteStatusBarLabel(creditNote)}{window.USING_PUBLIC_URL === true && " Credit Note"}</span></div>
                        <div className="data-panel with-status-bar" aria-label="Credit Note Details">
                            {
                                creditNote.service_company.logo && (
                                    <div className="data-panel__logo" aria-hidden="true">
                                        <div className="data-panel__logo__frame">
                                            <img src={creditNote.service_company.logo} alt="Logo" />
                                        </div>
                                    </div>
                                )
                            }
                            <div className="data-panel__form" aria-label="Credit Note Details">
                                <div className="data-panel__form__field" aria-label="Client">
                                    <span className="data-panel__form__field__label" aria-hidden="true">Client</span>
                                    <div className="data-panel__form__field__display-list" aria-label="Client">
                                        <div className="data-panel__form__field__display-list__object" aria-label="Client">
                                            <p>
                                                <strong>{renderClientString(creditNote.external_client)}</strong><br />
                                                {creditNote.external_client.billing_address_formatted_multiline && (
                                                    <span>{creditNote.external_client.billing_address_formatted_multiline}</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {
                                    creditNote.invoice !== null && (
                                        <AccessCheck entitlements={["entitlement_invoices_enabled"]} bypassChecks={window.USING_PUBLIC_URL === true}>
                                            <LinkedObjectsDisplayField
                                                fieldName="invoice"
                                                fieldLabel="Created From Invoice"
                                                objectComponentList={[
                                                    <InvoiceObjectLink
                                                        key="parent_invoice_0"
                                                        invoice={creditNote.invoice}
                                                        destination={window.USING_PUBLIC_URL === true ? window.PUBLIC_INVOICE_URL : DjangoUrls["invoices:invoices-detail"](window.MARKETPLACE_ENTITY_SLUG, creditNote.invoice.id)}
                                                        disabled={!(window.USING_PUBLIC_URL === true) && window.CURRENT_USER?.permissions.invoices_view_permission < PERMISSION_LEVEL.FULL}
                                                    />
                                                ]}
                                            ></LinkedObjectsDisplayField>
                                        </AccessCheck>
                                    )
                                }
                                {
                                    creditNote.invoice !== null && (
                                        <BasicDisplayField
                                            fieldName="service_client"
                                            fieldLabel="Service Client"
                                            fieldValue={renderClientString(creditNote.invoice.service_location.external_client)}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.invoice !== null && (
                                        <BasicDisplayField
                                            fieldName="service_location"
                                            fieldLabel="Service Location"
                                            fieldValue={renderServiceLocationString(creditNote.invoice.service_location, true)}
                                        ></BasicDisplayField>
                                    )
                                }
                                <BasicDisplayField
                                    fieldName="id"
                                    fieldLabel="Roopairs Credit Note ID"
                                    fieldValue={creditNote.custom_id ? creditNote.custom_id : creditNote.id}
                                ></BasicDisplayField>
                                {
                                    !window.USING_PUBLIC_URL && creditNote.labels.length !== 0 && (
                                        <BasicDisplayField
                                            fieldName="labels"
                                            fieldLabel={<Fragment>Labels <span className="text-optional">(Only Visible to You)</span></Fragment>}
                                            fieldValue={creditNote.labels.map((label, index) => <span key={`label_${index}`} className="data-tag">{label.name}</span>)}
                                        ></BasicDisplayField>
                                    )
                                }
                                <BasicDisplayField
                                    fieldName="date_issued"
                                    fieldLabel="Date Issued"
                                    fieldValue={renderInvoiceEstimateDate(creditNote.date_issued, creditNote.service_company.preferred_timezone)}
                                ></BasicDisplayField>
                                <BasicDisplayField
                                    fieldName="reason"
                                    fieldLabel="Reason"
                                    fieldValue={creditNote.reason || "\u2014"}
                                ></BasicDisplayField>
                                {
                                    !window.USING_PUBLIC_URL && creditNote.last_viewed !== null && (
                                        <BasicDisplayField
                                            fieldName="last_viewed"
                                            fieldLabel="Last Seen"
                                            fieldValue={this.renderLastViewed(creditNote.last_viewed, creditNote.service_company.preferred_timezone)}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.last_status_transition_time !== null && (
                                        <BasicDisplayField
                                            fieldName="last_status_transition_time"
                                            fieldLabel={`${creditNote.state_label} On`}
                                            fieldValue={this.renderLastViewed(creditNote.last_status_transition_time, creditNote.service_company.preferred_timezone)}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.last_status_transition_message !== "" && (
                                        <BasicDisplayField
                                            fieldName="last_status_transition_message"
                                            fieldLabel={`${creditNote.state_label} Message`}
                                            fieldValue={creditNote.last_status_transition_message}
                                        ></BasicDisplayField>
                                    )
                                }
                                {!(window.USING_PUBLIC_URL === true) && (
                                    <BasicDisplayField
                                        fieldName="last_modified_at"
                                        fieldLabel="Last Modified"
                                        fieldValue={this.renderLastViewed(creditNote.last_modified_at, creditNote.service_company.preferred_timezone)}
                                    ></BasicDisplayField>
                                )}
                                <LineItemListDisplayGroup
                                    object={creditNote}
                                    formatCurrencyValue={currencyFormatterForServiceCompany(creditNote.service_company)}
                                    useTaxes={creditNote.service_company.use_taxes}
                                    isCreditNoteLineItem={true}
                                    showPricebookPartDisplay={true}
                                ></LineItemListDisplayGroup>
                                {
                                    creditNote.service_company.use_taxes && creditNote.tax_name && (
                                        <BasicDisplayField
                                            fieldName="tax_name"
                                            fieldLabel="Tax Type"
                                            fieldValue={<span title={creditNote.tax_breakdown.map(component => `[${component.rate}%] ${component.name}`).join("\n")}>[{creditNote.tax_percent}%] {creditNote.tax_name}</span>}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.service_company.credit_note_fine_print !== "" && (
                                        <BasicDisplayField
                                            fieldName="credit_note_fine_print"
                                            fieldLabel={creditNote.service_company.credit_note_fine_print_label}
                                            fieldValue={creditNote.service_company.credit_note_fine_print}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.attachments.length !== 0 && creditNote.service_company.feature_file_uploads_enabled && (
                                        <BasicDisplayField
                                            fieldName="attachments"
                                            fieldLabel="Attachments"
                                            fieldValue={<AttachmentHorizontalDisplay
                                                attachments={creditNote.attachments}
                                                attachmentsEndpoint={DjangoUrls["invoices:api-credit-notes-attachments-list"](window.MARKETPLACE_ENTITY_SLUG, creditNote.id)}
                                                objectType="credit_note"
                                                isPublic={window.USING_PUBLIC_URL === true}
                                                    />}
                                            >
                                        </BasicDisplayField>)
                                }
                                {
                                    creditNote.accounting_sync_status !== null && !(window.USING_PUBLIC_URL === true) && (
                                        <BasicDisplayField
                                            fieldName="accounting_sync_status"
                                            fieldLabel="Accounting Sync Status"
                                            fieldValue={creditNote.accounting_sync_status.needs_syncing ? "Not Synced" : `Synced on ${this.renderLastViewed(creditNote.accounting_sync_status.last_synced_at, creditNote.service_company.preferred_timezone)}`}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    creditNote.accounting_sync_status !== null && creditNote.service_company.accounting_create_invoice_inventory_adjustments && !(window.USING_PUBLIC_URL === true) && (
                                        <BasicDisplayField
                                            fieldName="accounting_inventory_adjustment_sync_status"
                                            fieldLabel="Inventory Adjustment Journal Entry Sync Status"
                                            fieldValue={creditNote.accounting_inventory_adjustment_sync_status === null ? "Not Required" : creditNote.accounting_inventory_adjustment_sync_status.needs_syncing ? "Not Synced" : `Synced on ${this.renderLastViewed(creditNote.accounting_inventory_adjustment_sync_status.last_synced_at, creditNote.service_company.preferred_timezone)}`}
                                        ></BasicDisplayField>
                                    )
                                }
                                {
                                    !(window.USING_PUBLIC_URL === true) && creditNote.credit_allocations?.length !== 0 && (
                                        <AccessCheck
                                            entitlements={["entitlement_invoices_enabled"]}
                                            permissions={{
                                                invoices_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                            }}
                                        >
                                            <LinkedObjectsDisplayField
                                                fieldName="credit_allocations"
                                                fieldLabel="Applied To Invoices"
                                                objectComponentList={creditNote.credit_allocations.map((allocation, index) => (
                                                    <InvoiceObjectLink
                                                        key={`allocation_${index}`}
                                                        invoice={{
                                                            ...allocation,
                                                            id: allocation.invoice_id,
                                                            custom_id: allocation.invoice_custom_id,
                                                            status_label: allocation.invoice_status_label,
                                                            total: allocation.amount,
                                                            service_company: creditNote.service_company,
                                                        }}
                                                        destination={DjangoUrls["invoices:invoices-detail"](window.MARKETPLACE_ENTITY_SLUG, allocation.invoice_id)}
                                                        disabled={window.CURRENT_USER?.permissions.invoices_view_permission < PERMISSION_LEVEL.FULL}
                                                    />
                                                ))}
                                            ></LinkedObjectsDisplayField>
                                        </AccessCheck>
                                    )
                                }
                                <BasicDisplayField
                                    fieldName="applied_to_balance"
                                    fieldLabel="Applied To Client Balance"
                                    fieldValue={formatCurrencyForServiceCompany(appliedToBalance, creditNote.service_company)}
                                ></BasicDisplayField>
                            </div>
                            <div className="data-panel__form data-panel__data-summary amount-summary" aria-label="Amount Summary">
                                <hr aria-hidden="true" />
                                <div className="data-panel__data-summary__row amount-summary__item" aria-label="Subtotal">
                                    <div className="data-panel__data-summary__data__label"><span>Subtotal</span></div>
                                    <div className="data-panel__data-summary__data__value">{formatCurrencyForServiceCompany(creditNote.subtotal, creditNote.service_company)}</div>
                                </div>
                                {
                                    creditNote.service_company.use_taxes && (
                                        <div className="data-panel__data-summary__row amount-summary__item" aria-label="Tax">
                                            <div className="data-panel__data-summary__data__label"><span>Tax ({creditNote.tax_name} {creditNote.tax_percent}%)</span></div>
                                            <div className="data-panel__data-summary__data__value">{formatCurrencyForServiceCompany(creditNote.tax_amount, creditNote.service_company)}</div>
                                        </div>
                                    )
                                }
                                <div className="data-panel__data-summary__row amount-summary__item amount-summary__item--total" aria-label="Total">
                                    <div className="data-panel__data-summary__data__label"><span>Total</span></div>
                                    <div className="data-panel__data-summary__data__value">{formatCurrencyForServiceCompany(creditNote.total, creditNote.service_company)}</div>
                                </div>
                            </div>
                            {
                                Object.keys(errors).length !== 0 && (
                                    <Banner type="danger" text="This credit note has errors and cannot be created. Please edit the draft to fix them." extraMargin={true} />
                                )
                            }
                            {this.renderButtons()}
                            <PDFDownloadDialog
                                isOpen={this.state.isPDFDialogOpen}
                                onOpenChange={(newValue) => this.setState({ isPDFDialogOpen: newValue })}
                                objects={[
                                    'Credit Note',
                                    creditNote.invoice && 'Invoice',
                                ]}
                                onDownload={this.handlePDFDownload}
                                showToggle={creditNote.service_company.slug === window.CURRENT_USER?.service_company?.slug}
                            />
                        </div>
                    </div>
                </div>
                {!(window.USING_PUBLIC_URL === true) &&
                    <div className="accordion-wrapper">
                        <AccessCheck permissions={{
                            invoices_view_permission: PERMISSION_LEVEL.FULL
                        }}>
                            <CreditNoteFeedAccordion creditNoteID={creditNote.id} />
                        </AccessCheck>
                    </div>
                }
                {window.USING_PUBLIC_URL === true &&
                    <div className="powered-by-panel">
                        <span>Powered By</span>
                        <img className="logo--grey" src={window.LOGOTYPE_URL} width="148px" alt="Roopairs"></img>
                    </div>
                }
            </Fragment>
        )
    }

}

export default CreditNoteDetailsCard;
